import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

type BillingCycle = 'monthly' | 'annual';

interface Plan {
  id: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  tagline: string;
  description: string;
  popular?: boolean;
  accentClass: string;
  badgeBg: string;
  buttonClass: string;
  features: string[];
  highlight: string;
}

const PLANS: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 47,
    annualPrice: 470,
    tagline: 'For Beginners',
    description: 'Perfect for new investors ready to explore foreclosure opportunities nationwide.',
    accentClass: 'border-gray-200',
    badgeBg: '',
    buttonClass: 'bg-navy text-white hover:bg-navy-light',
    highlight: 'Great for beginners',
    features: [
      'Nationwide property maps & listings',
      'Basic deal alerts (weekly digest)',
      'Community forum access',
      '5 saved properties',
      'Beginner investment guides',
      'Email support',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    monthlyPrice: 97,
    annualPrice: 970,
    tagline: 'Most Popular',
    description: 'Our flagship plan for serious investors targeting the full nationwide market.',
    popular: true,
    accentClass: 'border-gold',
    badgeBg: 'bg-gold',
    buttonClass: 'bg-gold text-navy-dark hover:bg-gold-light',
    highlight: 'Best value for active investors',
    features: [
      'All 50 states — full nationwide access',
      'Priority deal alerts (daily)',
      'Deal tracker & pipeline manager',
      'Tax records database access',
      'Full backoffice dashboard',
      '50 saved properties',
      'Monthly expert webinars',
      'Advanced ROI analytics',
      'Priority email & chat support',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: 197,
    annualPrice: 1970,
    tagline: 'Full Service',
    description: 'The ultimate hands-on experience for high-volume investors and syndicators.',
    accentClass: 'border-navy',
    badgeBg: 'bg-navy',
    buttonClass: 'bg-navy text-white hover:bg-navy-light',
    highlight: 'White-glove experience',
    features: [
      'Everything in Professional',
      '1-on-1 coaching calls (monthly)',
      'Custom deal analysis reports',
      'Private mastermind group',
      'White-glove support team',
      'Unlimited saved properties',
      'Dedicated acquisition specialist',
      'Exclusive Enterprise-only deal flow',
      'Quarterly portfolio strategy review',
      'Direct legal team access',
    ],
  },
];

interface CompareCategory {
  label: string;
  rows: { feature: string; starter: string | boolean; professional: string | boolean; enterprise: string | boolean }[];
}

const COMPARE_TABLE: CompareCategory[] = [
  {
    label: 'Property Access',
    rows: [
      { feature: 'State coverage', starter: 'Select states', professional: 'All 50 states', enterprise: 'All 50 states' },
      { feature: 'Property maps', starter: 'Basic maps', professional: 'Full nationwide maps', enterprise: 'Full nationwide maps' },
      { feature: 'Saved properties', starter: '5 properties', professional: '50 properties', enterprise: 'Unlimited' },
      { feature: 'Deal alerts', starter: 'Weekly digest', professional: 'Daily priority alerts', enterprise: 'Instant alerts' },
      { feature: 'Pre-market access', starter: false, professional: '30–60 days early', enterprise: '60–90 days early' },
    ],
  },
  {
    label: 'Research & Tools',
    rows: [
      { feature: 'Tax records database', starter: false, professional: true, enterprise: true },
      { feature: 'Deal tracker & pipeline', starter: false, professional: true, enterprise: true },
      { feature: 'Full backoffice dashboard', starter: false, professional: true, enterprise: true },
      { feature: 'AI-powered ROI analytics', starter: false, professional: true, enterprise: true },
      { feature: 'Custom deal analysis reports', starter: false, professional: false, enterprise: true },
      { feature: 'Title & lien reports', starter: 'Basic', professional: 'Detailed', enterprise: 'Detailed + custom' },
    ],
  },
  {
    label: 'Community & Education',
    rows: [
      { feature: 'Community forum', starter: true, professional: true, enterprise: true },
      { feature: 'Investment guides library', starter: 'Starter library', professional: 'Full library', enterprise: 'Full library' },
      { feature: 'Monthly expert webinars', starter: false, professional: true, enterprise: true },
      { feature: 'Private mastermind group', starter: false, professional: false, enterprise: true },
      { feature: '1-on-1 coaching calls', starter: false, professional: false, enterprise: 'Monthly' },
    ],
  },
  {
    label: 'Support',
    rows: [
      { feature: 'Email support', starter: 'Standard', professional: 'Priority', enterprise: 'White-glove' },
      { feature: 'Live chat support', starter: false, professional: true, enterprise: true },
      { feature: 'Dedicated specialist', starter: false, professional: false, enterprise: true },
      { feature: 'Legal team access', starter: false, professional: false, enterprise: true },
      { feature: 'Quarterly portfolio review', starter: false, professional: false, enterprise: true },
    ],
  },
];

const TESTIMONIAL_SNIPPETS = [
  { name: 'Michael C.', tier: 'Enterprise', text: 'The coaching calls alone are worth 10x the price.', roi: '340%' },
  { name: 'Sandra W.', tier: 'Enterprise', text: 'White-glove support means I never feel stuck on a deal.', roi: '280%' },
  { name: 'David R.', tier: 'Professional', text: 'Tax records database + deal tracker = unfair advantage.', roi: '195%' },
];

function CheckIcon() {
  return (
    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
      <i className="ri-check-line text-green-500 text-base font-bold" />
    </div>
  );
}

function XIcon() {
  return (
    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
      <i className="ri-close-line text-gray-300 text-base" />
    </div>
  );
}

function CellValue({ val }: { val: string | boolean }) {
  if (val === true) return (
    <div className="flex justify-center">
      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-green-50">
        <i className="ri-check-line text-green-600 text-sm font-bold" />
      </div>
    </div>
  );
  if (val === false) return (
    <div className="flex justify-center">
      <i className="ri-subtract-line text-gray-300 text-base" />
    </div>
  );
  return <span className="text-sm text-navy font-medium">{val}</span>;
}

export default function Pricing() {
  const [billing, setBilling] = useState<BillingCycle>('annual');

  const getDisplayPrice = (plan: Plan) =>
    billing === 'annual' ? plan.annualPrice : plan.monthlyPrice;

  const getPriceSuffix = () => (billing === 'annual' ? '/year' : '/mo');

  const getSavingsNote = (plan: Plan) => {
    const annualEquivalentMonthly = plan.monthlyPrice * 12;
    const saved = annualEquivalentMonthly - plan.annualPrice;
    return `Saves $${saved.toLocaleString()} vs monthly`;
  };

  return (
    <div className="font-sans">
      <Navbar />
      <main>
        {/* ── Hero ── */}
        <section className="relative bg-navy pt-40 pb-28 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/5c3084ad47a4fe635e60e93c30f2f6e9.jpg"
              alt="Pricing plans"
              className="w-full h-full object-cover object-top opacity-10"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/40 to-navy" />
          </div>

          <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 text-center">
            <span className="inline-block px-4 py-1.5 border border-gold/50 text-gold text-xs font-semibold tracking-widest uppercase rounded-full mb-6">
              Simple, Transparent Pricing
            </span>
            <h1 className="font-playfair text-5xl md:text-7xl font-bold text-white mb-5 leading-tight">
              Invest in Your<br />
              <span className="text-gold">Competitive Edge</span>
            </h1>
            <p className="text-white/65 text-xl max-w-2xl mx-auto mb-10">
              Choose the plan that matches your ambition. Every tier starts with a <strong className="text-white">14-day free trial</strong> — no credit card required.
            </p>

            {/* Billing toggle */}
            <div className="inline-flex items-center gap-1 bg-white/10 border border-white/20 rounded-full p-1 backdrop-blur-sm">
              <button
                onClick={() => setBilling('monthly')}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  billing === 'monthly'
                    ? 'bg-white/20 text-white border border-white/30'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBilling('annual')}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  billing === 'annual'
                    ? 'bg-gold text-navy-dark'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                Annual
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${
                  billing === 'annual'
                    ? 'bg-navy text-gold'
                    : 'bg-gold/30 text-gold'
                }`}>
                  Save 17%
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* ── Pricing Cards ── */}
        <section className="bg-cream py-20">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
              {PLANS.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative flex flex-col rounded-2xl border-2 overflow-hidden transition-all duration-300 ${plan.accentClass} ${
                    plan.popular ? 'md:-mt-6 md:mb-0' : ''
                  } bg-white`}
                >
                  {/* Popular badge */}
                  {plan.popular && (
                    <div className={`${plan.badgeBg} text-navy-dark text-xs font-bold text-center py-2.5 tracking-widest uppercase`}>
                      ★ Most Popular
                    </div>
                  )}

                  <div className="flex flex-col flex-1 p-8">
                    {/* Header */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-1">
                        <h2 className="font-playfair text-2xl font-bold text-navy">{plan.name}</h2>
                        {!plan.popular && plan.badgeBg && (
                          <span className={`px-2.5 py-1 ${plan.badgeBg} text-white text-xs font-bold rounded-full whitespace-nowrap`}>
                            {plan.tagline}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 text-sm leading-relaxed mt-2">{plan.description}</p>
                    </div>

                    {/* Price */}
                    <div className="mb-2">
                      <div className="flex items-end gap-1">
                        <span className="font-playfair text-6xl font-bold text-navy leading-none">
                          ${getDisplayPrice(plan).toLocaleString()}
                        </span>
                        <span className="text-gray-400 text-sm mb-2">{getPriceSuffix()}</span>
                      </div>
                      {billing === 'annual' ? (
                        <p className="text-green-600 text-xs font-semibold mt-1 flex items-center gap-1">
                          <i className="ri-calendar-check-line" />
                          {getSavingsNote(plan)} — billed annually
                        </p>
                      ) : (
                        <p className="text-amber-600 text-xs font-semibold mt-1 flex items-center gap-1">
                          <i className="ri-information-line" />
                          Switch to Annual and save 17%
                        </p>
                      )}
                    </div>

                    {/* Highlight tag */}
                    <div className="mb-6 mt-1">
                      <span className="text-xs text-gold font-semibold flex items-center gap-1">
                        <i className="ri-star-line text-gold" />
                        {plan.highlight}
                      </span>
                    </div>

                    {/* CTA Button */}
                    <Link
                      to="/contact"
                      className={`block text-center py-4 rounded-xl font-bold text-sm transition-all duration-200 whitespace-nowrap mb-8 ${plan.buttonClass}`}
                    >
                      Join Now — Start Free Trial
                    </Link>

                    {/* Divider */}
                    <div className="border-t border-gray-100 mb-6" />

                    {/* Features */}
                    <ul className="flex flex-col gap-3 flex-1">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckIcon />
                          <span className="text-gray-600 text-sm leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Free trial note */}
                    <p className="text-gray-400 text-xs text-center mt-8 pt-6 border-t border-gray-50">
                      14-day free trial · {billing === 'annual' ? 'Billed annually' : 'Billed monthly'} · No credit card required
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Money back */}
            <div className="mt-10 flex items-center justify-center gap-3 text-sm text-gray-500">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-green-100">
                <i className="ri-shield-check-line text-green-600 text-base" />
              </div>
              <span>
                <strong className="text-navy">30-day money-back guarantee</strong> on all plans. If you&apos;re not satisfied, we&apos;ll refund you in full — no questions asked.
              </span>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 flex items-start gap-3 px-5 py-4 bg-amber-50 border border-amber-200 rounded-xl max-w-4xl mx-auto">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-amber-600 text-base" />
              </div>
              <p className="text-amber-800 text-sm leading-relaxed">
                <span className="font-bold">Important:</span> All tax lien certificates and tax deeds are purchased directly through your county government office. Foreclosure Recovery provides research, data, and acquisition forms only — we are not a broker, dealer, or seller of tax certificates.
              </p>
            </div>
          </div>
        </section>

        {/* ── Mini Testimonials ── */}
        <section className="bg-white py-14">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {TESTIMONIAL_SNIPPETS.map((t, i) => (
                <div key={i} className="flex gap-4 items-start p-6 rounded-xl bg-cream border border-gray-100">
                  <div className="text-gold text-3xl font-playfair leading-none mt-1 flex-shrink-0">&ldquo;</div>
                  <div>
                    <p className="text-navy text-sm font-medium leading-relaxed mb-3">{t.text}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-navy">{t.name}</span>
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                        t.tier === 'Enterprise' ? 'bg-navy text-gold' : 'bg-gold/20 text-gold-dark'
                      }`}>{t.tier}</span>
                      <span className="text-xs text-green-600 font-semibold ml-auto flex items-center gap-0.5">
                        <i className="ri-arrow-up-line text-xs" />{t.roi} ROI
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Feature Comparison Table ── */}
        <section className="bg-cream py-20">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            {/* Header */}
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-navy/10 text-navy text-xs font-semibold tracking-widest uppercase rounded-full mb-4">
                Full Breakdown
              </span>
              <h2 className="font-playfair text-4xl md:text-5xl font-bold text-navy">
                Compare All Features
              </h2>
              <p className="text-gray-500 mt-4 max-w-lg mx-auto">
                Every detail, side by side. Pick the plan that fits your strategy.
              </p>
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {/* Sticky header row */}
              <div className="grid grid-cols-4 border-b-2 border-gray-100 sticky top-20 bg-white z-10">
                <div className="py-5 px-6 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  Feature
                </div>
                {PLANS.map((plan) => (
                  <div
                    key={plan.id}
                    className={`py-5 px-4 text-center ${plan.popular ? 'bg-gold/5' : ''}`}
                  >
                    <p className={`font-playfair text-xl font-bold ${plan.popular ? 'text-gold' : 'text-navy'}`}>
                      {plan.name}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      ${getDisplayPrice(plan).toLocaleString()}{getPriceSuffix()}
                    </p>
                    {plan.popular && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-gold/20 text-gold text-xs font-bold rounded-full whitespace-nowrap">
                        Most Popular
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Category sections */}
              {COMPARE_TABLE.map((category, catIdx) => (
                <div key={catIdx}>
                  {/* Category header */}
                  <div className="grid grid-cols-4 bg-navy/5 border-b border-gray-100">
                    <div className="col-span-4 py-3 px-6 text-xs font-bold text-navy/60 uppercase tracking-widest">
                      {category.label}
                    </div>
                  </div>

                  {/* Rows */}
                  {category.rows.map((row, rowIdx) => (
                    <div
                      key={rowIdx}
                      className={`grid grid-cols-4 border-b border-gray-50 hover:bg-cream/60 transition-colors duration-150 ${
                        rowIdx === category.rows.length - 1 ? 'border-b-0' : ''
                      }`}
                    >
                      {/* Feature label */}
                      <div className="py-4 px-6 flex items-center">
                        <span className="text-sm text-gray-700">{row.feature}</span>
                      </div>

                      {/* Starter */}
                      <div className="py-4 px-4 flex items-center justify-center">
                        <CellValue val={row.starter} />
                      </div>

                      {/* Professional */}
                      <div className="py-4 px-4 flex items-center justify-center bg-gold/5">
                        <CellValue val={row.professional} />
                      </div>

                      {/* Enterprise */}
                      <div className="py-4 px-4 flex items-center justify-center">
                        <CellValue val={row.enterprise} />
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {/* Footer CTA row */}
              <div className="grid grid-cols-4 border-t-2 border-gray-100 bg-cream/50">
                <div className="py-6 px-6 flex items-center">
                  <span className="text-sm font-semibold text-navy">Ready to start?</span>
                </div>
                {PLANS.map((plan) => (
                  <div key={plan.id} className={`py-6 px-4 flex items-center justify-center ${plan.popular ? 'bg-gold/5' : ''}`}>
                    <Link
                      to="/contact"
                      className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 whitespace-nowrap ${plan.buttonClass}`}
                    >
                      Join {plan.name}
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ Strip ── */}
        <section className="bg-white py-16">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { q: 'Can I switch plans anytime?', a: 'Yes — upgrade instantly or downgrade at your next renewal. You can also switch between monthly and annual billing at any time.' },
                { q: 'Is there really no credit card needed?', a: 'Correct. Your 14-day free trial starts immediately with zero payment info required.' },
                { q: 'What happens after the trial?', a: "You'll get a reminder email before the trial ends. Only pay if you choose to continue — then your chosen billing cycle (monthly or annual) activates." },
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gold/10 flex-shrink-0 mt-1">
                    <i className="ri-question-line text-gold text-sm" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-navy text-sm mb-1">{item.q}</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Bottom CTA ── */}
        <section className="bg-navy py-24">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
              <div>
                <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white leading-tight">
                  Still not sure which<br />plan is right for you?
                </h2>
                <p className="text-white/60 mt-4 text-lg max-w-md">
                  Talk to one of our investment specialists. We&apos;ll match you with the perfect plan for your goals and market focus.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gold text-navy-dark font-bold rounded-xl hover:bg-gold-light transition-colors whitespace-nowrap text-base"
                >
                  Talk to a Specialist
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/how-it-works"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/30 text-white font-semibold rounded-xl hover:border-gold hover:text-gold transition-colors whitespace-nowrap text-base"
                >
                  See How It Works
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
